// src/components/job-engine/PayloadEditor.tsx
import React, { Suspense } from "react";
import { Spinner } from "@fluentui/react";

const LazyEditor = React.lazy(async () => {
  const { EditorView, basicSetup } = await import("codemirror");
  const { EditorState } = await import("@codemirror/state");
  const { json } = await import("@codemirror/lang-json");

  interface InnerProps {
    value: string;
    onChange: (next: string) => void;
    height?: number;
  }

  const Inner: React.FC<InnerProps> = ({ value, onChange, height = 240 }) => {
    const hostRef = React.useRef<HTMLDivElement>(null);
    const viewRef = React.useRef<InstanceType<typeof EditorView> | null>(null);

    React.useEffect(() => {
      if (!hostRef.current) return;
      const fontTheme = EditorView.theme({
        "&": { fontSize: "13px" },
        ".cm-content": { fontFamily: "Consolas, Menlo, 'Liberation Mono', monospace" },
        ".cm-gutters": { fontSize: "13px" },
      });

      const state = EditorState.create({
        doc: value,
        extensions: [
          basicSetup,
          json(),
          fontTheme,
          EditorView.updateListener.of((u) => {
            if (u.docChanged) onChange(u.state.doc.toString());
          }),
        ],
      });
      const view = new EditorView({ state, parent: hostRef.current });
      viewRef.current = view;
      return () => view.destroy();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // External value updates (e.g. "reset" action) pushed into the editor
    React.useEffect(() => {
      const view = viewRef.current;
      if (!view) return;
      const cur = view.state.doc.toString();
      if (cur !== value) {
        view.dispatch({ changes: { from: 0, to: cur.length, insert: value } });
      }
    }, [value]);

    return (
      <div
        ref={hostRef}
        style={{
          height,
          border: "1px solid #edebe9",
          borderRadius: 4,
          fontSize: 13,
          fontFamily: "Consolas, Menlo, 'Liberation Mono', monospace",
        }}
      />
    );
  };
  return { default: Inner };
});

interface Props {
  value: string;
  onChange: (next: string) => void;
  height?: number;
}

const PayloadEditor: React.FC<Props> = (props) => (
  <Suspense fallback={<Spinner label="Loading editor..." />}>
    <LazyEditor {...props} />
  </Suspense>
);

export default PayloadEditor;
